import { Icon, SelectBoxInput } from '@aqarmap/aqarmap-ui';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetAllPropertyTypeQuery } from '~/redux/api/userApiSlice';
import { AddListingInitializeType, PropertiesType } from '~addListing/types';
import {
  FieldErrors,
  UseFormClearErrors,
  UseFormSetValue,
} from 'react-hook-form';
import PropertyTypeList from '~addListing/pages/InitializeStep/PropertyTypeList';
import { useParams } from 'react-router-dom';

const PropertyType = ({
  setValue,
  errors,
  clearErrors,
}: {
  setValue: UseFormSetValue<AddListingInitializeType>;
  errors: FieldErrors<AddListingInitializeType>;
  clearErrors: UseFormClearErrors<AddListingInitializeType>;
}) => {
  const { t } = useTranslation('app');
  const { locale } = useParams();
  const [properties, setProperties] = useState<PropertiesType[]>([]);
  const [selectedProperty, setSelectedProperty] =
    useState<PropertiesType | null>(null);
  const [showContent, setShowContent] = useState(false);
  const [isError, setIsError] = useState(false);
  const { data, isSuccess, refetch } = useGetAllPropertyTypeQuery(null);
  const dropdownMenuRef = useRef<HTMLDivElement>(null);

  const handlePropertyTypeSearch = (keyword: string) => {
    const cloneData: PropertiesType[] = [...data.data];

    const getItemsWithChildren = () => {
      let result: PropertiesType[] = [];

      const recurse = (nodes: PropertiesType[]) => {
        nodes.forEach((node) => {
          result.push(node);
          if (node.children?.length > 0) {
            recurse(node.children);
          }
        });
      };

      recurse(cloneData);
      return result;
    };

    const itemsWithChildren = getItemsWithChildren();
    const _properties = itemsWithChildren.filter((item) =>
      item.singularName.toLowerCase().includes(keyword.trim().toLowerCase()),
    );
    setProperties(_properties);
  };

  useEffect(() => {
    if (isSuccess) setProperties(data.data);
  }, [isSuccess]);

  useEffect(() => {
    if (selectedProperty) {
      setValue('propertyType', selectedProperty.id);
    }
  }, [selectedProperty]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownMenuRef.current &&
        !dropdownMenuRef.current.contains(e.target as Node)
      ) {
        setShowContent(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (errors.propertyType) setIsError(true);
    else setIsError(false);
  }, [errors, showContent]);

  useEffect(() => {
    refetch()
      .unwrap()
      .then((data) => setProperties(data.data));
  }, [locale]);

  return (
    <div className="w-full flex flex-col gap-1.5x z-10">
      <SelectBoxInput
        open={showContent}
        setOpen={setShowContent}
        label={{
          className: 'text-title_4 text-black',
          text: t('propertyType'),
        }}
        error={isError ? t('requiredPropertyType') : ''}
        field={{
          placeholder: t('choosePropertyType'),
          value: selectedProperty?.singularName,
        }}
        onOpen={() => clearErrors('propertyType')}
      >
        <div className="max-h-[216px]">
          <div className="flex gap-x items-center border border-gray rounded-[12px] px-1.5x py-x text-gray__dark_1 mt-1.5x mb-0.5x mx-1.5x">
            <Icon name="search" size="24px" />
            <input
              type="text"
              className="flex-1"
              placeholder={t('search')}
              onChange={(e) => handlePropertyTypeSearch(e.target.value)}
            />
          </div>
          <div className="overflow-y-auto pb-1.5x max-h-[160px]">
            {properties.length === 0 ? (
              <p className="px-1.5x pb-x pt-0.5x text-body_2 text-gray__dark_2">
                {t('noResults')}
              </p>
            ) : (
              <PropertyTypeList
                properties={properties}
                setSelectedProperty={setSelectedProperty}
                setShowContent={setShowContent}
                className="text-link text-black"
              />
            )}
          </div>
        </div>
      </SelectBoxInput>
    </div>
  );
};

export default PropertyType;
